import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft,
    Briefcase,
    CheckCircle2,
    Download,
    FileText,
    Loader2,
    Mail,
    Plus,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { GROWTH_SERVICES } from "@/lib/growthServices";
import AdminShell from "@/components/AdminShell";
import ChatPanel from "@/components/ChatPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminClientDetail() {
    const { id } = useParams();
    const { user, token, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [showNewProject, setShowNewProject] = useState(false);
    const [showNewInvoice, setShowNewInvoice] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [team, setTeam] = useState([]);
    const [editForm, setEditForm] = useState({
        plan: "",
        plan_summary: "",
        manager_name: "",
        manager_role: "",
        manager_email: "",
    });

    const [projectForm, setProjectForm] = useState({
        title: "",
        summary: "",
        status: "active",
        services: [],
    });
    const [invoiceForm, setInvoiceForm] = useState({
        title: "",
        amount: "",
        currency: "usd",
        status: "sent",
        payment_instructions: "",
    });

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    const refresh = () =>
        Promise.all([
            axios.get(`${API}/admin/clients/${id}`, { headers }),
            axios.get(`${API}/admin/team`, { headers }).catch(() => ({ data: [] })),
        ])
            .then(([r, t]) => {
                setData(r.data);
                setTeam(t.data || []);
                setEditForm({
                    plan: r.data.client.plan || "",
                    plan_summary: r.data.client.plan_summary || "",
                    manager_name: r.data.client.manager_name || "",
                    manager_role: r.data.client.manager_role || "",
                    manager_email: r.data.client.manager_email || "",
                });
            })
            .catch(() => toast.error("Could not load client"))
            .finally(() => setBusy(false));

    useEffect(() => {
        if (token) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, id]);

    const onResendInvite = async () => {
        try {
            await axios.post(
                `${API}/admin/clients/${id}/invite`,
                {},
                { headers },
            );
            toast.success("Invite re-sent");
        } catch {
            toast.error("Could not send invite");
        }
    };

    const onApprovePending = async () => {
        try {
            await axios.post(
                `${API}/admin/clients/${id}/approve`,
                {},
                { headers },
            );
            toast.success("Client approved — they can now sign in");
            refresh();
        } catch {
            toast.error("Could not approve client");
        }
    };

    const assignManager = async (memberId) => {
        try {
            const { data: r } = await axios.post(
                `${API}/admin/clients/${id}/assign-manager`,
                { member_id: memberId || null },
                { headers },
            );
            toast.success(`Assigned to ${r.assigned.name}`);
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Assign failed");
        }
    };

    const unassignManager = async () => {
        try {
            await axios.post(
                `${API}/admin/clients/${id}/unassign-manager`,
                {},
                { headers },
            );
            toast.success("Manager unassigned");
            refresh();
        } catch {
            toast.error("Could not unassign");
        }
    };

    const onCreateProject = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...projectForm, client_id: id };
            // Backend stores services as free-form labels; map ids → labels.
            payload.services = (projectForm.services || []).map((sid) => {
                const svc = GROWTH_SERVICES.find((x) => x.id === sid);
                return svc ? svc.label : sid;
            });
            const { data: p } = await axios.post(
                `${API}/admin/projects`,
                payload,
                { headers },
            );
            toast.success("Project created");
            setShowNewProject(false);
            setProjectForm({ title: "", summary: "", status: "active", services: [] });
            navigate(`/admin/projects/${p.id}`);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not create project",
            );
        }
    };

    const toggleProjectService = (sid) => {
        setProjectForm((f) => {
            const set = new Set(f.services || []);
            if (set.has(sid)) set.delete(sid);
            else set.add(sid);
            return { ...f, services: Array.from(set) };
        });
    };

    const onCreateInvoice = async (e) => {
        e.preventDefault();
        const amount = parseFloat(invoiceForm.amount);
        if (!amount || amount <= 0) {
            toast.error("Amount must be greater than 0");
            return;
        }
        try {
            await axios.post(
                `${API}/admin/invoices`,
                { ...invoiceForm, amount, client_id: id },
                { headers },
            );
            toast.success("Invoice issued");
            setShowNewInvoice(false);
            setInvoiceForm({
                title: "",
                amount: "",
                currency: "usd",
                status: "sent",
                payment_instructions: "",
            });
            refresh();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not create invoice",
            );
        }
    };

    const onSaveEdit = async (e) => {
        e.preventDefault();
        try {
            await axios.patch(
                `${API}/admin/clients/${id}`,
                editForm,
                { headers },
            );
            toast.success("Client details updated");
            setShowEdit(false);
            refresh();
        } catch {
            toast.error("Could not save");
        }
    };

    const onMarkPaid = async (invId) => {
        if (!window.confirm("Mark this invoice as paid?")) return;
        try {
            await axios.post(
                `${API}/admin/invoices/${invId}/mark-paid`,
                { paid_method: "bank transfer" },
                { headers },
            );
            toast.success("Marked paid · receipt emailed to client");
            refresh();
        } catch {
            toast.error("Could not mark as paid");
        }
    };

    const onDownloadPdf = async (inv) => {
        try {
            const res = await axios.get(
                `${API}/admin/invoices/${inv.id}/pdf`,
                { headers, responseType: "blob" },
            );
            const url = URL.createObjectURL(
                new Blob([res.data], { type: "application/pdf" }),
            );
            const a = document.createElement("a");
            a.href = url;
            a.download = `${inv.number}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Could not download PDF");
        }
    };

    if (loading) return null;
    if (!user) return <Navigate to="/admin/login" replace />;
    if (busy)
        return (
            <AdminShell>
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </AdminShell>
        );
    if (!data) return null;

    const { client, projects, invoices } = data;

    return (
        <AdminShell>
            <Link
                to="/admin/clients"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-bone-300 hover:text-ember mb-8"
            >
                <ArrowLeft className="w-3.5 h-3.5" />
                All clients
            </Link>

            <div className="flex items-start justify-between gap-6 flex-wrap mb-10">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-3">
                        Client
                    </div>
                    <h1 className="font-display text-bone text-5xl tracking-tightest leading-[1]">
                        {client.name}
                    </h1>
                    <div className="mt-3 text-bone-300">
                        {client.company && (
                            <span className="mr-4">{client.company}</span>
                        )}
                        <a
                            href={`mailto:${client.email}`}
                            className="link-underline"
                        >
                            {client.email}
                        </a>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {client.status === "pending" && (
                        <button
                            onClick={onApprovePending}
                            data-testid="approve-client"
                            className="inline-flex items-center gap-2 h-11 px-4 text-xs uppercase tracking-[0.2em] bg-ember text-ink hover:bg-ember/90 transition-colors"
                        >
                            Approve account
                        </button>
                    )}
                    <button
                        onClick={() => setShowEdit(true)}
                        data-testid="edit-client"
                        className="btn-outline-bone inline-flex items-center gap-2 h-11 px-4 text-xs uppercase tracking-[0.2em]"
                    >
                        Edit details
                    </button>
                    <button
                        onClick={onResendInvite}
                        data-testid="resend-invite"
                        className="btn-outline-bone inline-flex items-center gap-2 h-11 px-4 text-xs uppercase tracking-[0.2em]"
                    >
                        <Mail className="w-4 h-4" />
                        Re-send invite
                    </button>
                </div>
            </div>

            {/* Plan + Manager preview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                <div className="border border-white/10 bg-ink-800/40 p-5">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                        Plan
                    </div>
                    {client.plan ? (
                        <>
                            <div className="font-display text-bone text-lg tracking-tight">
                                {client.plan}
                            </div>
                            {client.plan_summary && (
                                <p className="text-bone-300 text-sm mt-1 line-clamp-2">
                                    {client.plan_summary}
                                </p>
                            )}
                        </>
                    ) : (
                        <span className="text-bone-500 text-sm">
                            Not set
                        </span>
                    )}
                </div>
                <div className="border border-white/10 bg-ink-800/40 p-5">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                        Account manager
                    </div>
                    {client.manager_name || client.manager_email ? (
                        <>
                            <div className="font-display text-bone text-lg tracking-tight">
                                {client.manager_name || "—"}
                                {client.manager_role && (
                                    <span className="text-bone-500 text-xs uppercase tracking-[0.2em] ml-2">
                                        · {client.manager_role}
                                    </span>
                                )}
                            </div>
                            {client.manager_email && (
                                <a
                                    href={`mailto:${client.manager_email}`}
                                    className="text-ember text-sm hover:underline"
                                >
                                    {client.manager_email}
                                </a>
                            )}
                        </>
                    ) : (
                        <span className="text-bone-500 text-sm">
                            Not assigned
                        </span>
                    )}
                </div>
            </div>

            {/* Projects */}
            <section className="mb-12">
                <div className="flex items-center justify-between border-b border-white/10 pb-3 mb-5">
                    <h2 className="font-display text-bone text-2xl tracking-tight inline-flex items-center gap-2">
                        <Briefcase className="w-5 h-5" />
                        Projects
                    </h2>
                    <button
                        onClick={() => setShowNewProject(true)}
                        data-testid="new-project-button"
                        className="btn-ember inline-flex items-center gap-2 h-9 px-4 text-xs uppercase tracking-[0.2em]"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        New project
                    </button>
                </div>
                {projects.length === 0 ? (
                    <p className="text-bone-500 text-sm">No projects yet.</p>
                ) : (
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {projects.map((p) => (
                            <li key={p.id}>
                                <Link
                                    to={`/admin/projects/${p.id}`}
                                    data-testid={`project-row-${p.id}`}
                                    className="block border border-white/10 bg-ink-800/40 p-5 hover:border-ember/40 transition-colors"
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <h3 className="font-display text-bone text-xl tracking-tight">
                                            {p.title}
                                        </h3>
                                        <span className="text-[10px] uppercase tracking-[0.2em] text-bone-500">
                                            {p.status}
                                        </span>
                                    </div>
                                    {p.summary && (
                                        <p className="text-bone-300 text-sm line-clamp-2">
                                            {p.summary}
                                        </p>
                                    )}
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Invoices */}
            <section>
                <div className="flex items-center justify-between border-b border-white/10 pb-3 mb-5">
                    <h2 className="font-display text-bone text-2xl tracking-tight inline-flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Invoices
                    </h2>
                    <button
                        onClick={() => setShowNewInvoice(true)}
                        data-testid="new-invoice-button"
                        className="btn-ember inline-flex items-center gap-2 h-9 px-4 text-xs uppercase tracking-[0.2em]"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Issue invoice
                    </button>
                </div>
                {invoices.length === 0 ? (
                    <p className="text-bone-500 text-sm">No invoices yet.</p>
                ) : (
                    <ul className="border-t border-white/10">
                        {invoices.map((inv) => (
                            <li
                                key={inv.id}
                                data-testid={`admin-invoice-${inv.id}`}
                                className="border-b border-white/10 grid grid-cols-12 gap-3 items-center py-4"
                            >
                                <span className="col-span-2 text-xs uppercase tracking-[0.18em] text-bone-500">
                                    {inv.number}
                                </span>
                                <span className="col-span-4 font-display text-bone text-base tracking-tight truncate">
                                    {inv.title}
                                </span>
                                <span className="col-span-2 text-bone">
                                    {inv.amount.toFixed(2)}{" "}
                                    {inv.currency.toUpperCase()}
                                </span>
                                <span className="col-span-1 text-[10px] uppercase tracking-[0.2em] text-ember">
                                    {inv.status}
                                </span>
                                <div className="col-span-3 justify-self-end flex items-center gap-2">
                                    <button
                                        onClick={() => onDownloadPdf(inv)}
                                        data-testid={`admin-pdf-${inv.id}`}
                                        className="inline-flex items-center gap-1.5 h-9 px-3 border border-white/15 hover:border-ember hover:text-ember text-[10px] uppercase tracking-[0.2em] transition-colors"
                                    >
                                        <Download className="w-3.5 h-3.5" />
                                        PDF
                                    </button>
                                    {(inv.status === "sent" || inv.status === "overdue") && (
                                        <button
                                            onClick={() => onMarkPaid(inv.id)}
                                            data-testid={`mark-paid-${inv.id}`}
                                            className="btn-ember inline-flex items-center gap-1.5 h-9 px-3 text-[10px] uppercase tracking-[0.2em]"
                                        >
                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                            Mark paid
                                        </button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {showNewProject && (
                <Modal onClose={() => setShowNewProject(false)} title="New project">
                    <form
                        onSubmit={onCreateProject}
                        className="space-y-5"
                        data-testid="new-project-form"
                    >
                        <Input
                            label="Title"
                            testid="project-title"
                            value={projectForm.title}
                            onChange={(v) =>
                                setProjectForm((f) => ({ ...f, title: v }))
                            }
                        />
                        <Input
                            label="Summary"
                            testid="project-summary"
                            value={projectForm.summary}
                            onChange={(v) =>
                                setProjectForm((f) => ({ ...f, summary: v }))
                            }
                            multiline
                        />
                        <div data-testid="project-services-picker">
                            <p className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                                Growth services
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                                {GROWTH_SERVICES.map((s) => {
                                    const active = (projectForm.services || []).includes(s.id);
                                    return (
                                        <button
                                            type="button"
                                            key={s.id}
                                            data-testid={`project-service-${s.id}`}
                                            onClick={() => toggleProjectService(s.id)}
                                            className={`text-left px-3 py-2 border text-xs uppercase tracking-[0.18em] transition-colors ${
                                                active
                                                    ? "border-ember bg-ember/10 text-ember"
                                                    : "border-white/15 text-bone-300 hover:border-white/30"
                                            }`}
                                        >
                                            {s.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="mt-2 text-[10px] text-bone-500">
                                Tags shown on client + manager project pages. Multi-select.
                            </p>
                        </div>
                        <button
                            type="submit"
                            data-testid="project-submit"
                            className="btn-ember w-full h-12 text-xs uppercase tracking-[0.2em]"
                        >
                            Create project
                        </button>
                    </form>
                </Modal>
            )}

            {showNewInvoice && (
                <Modal onClose={() => setShowNewInvoice(false)} title="Issue invoice">
                    <form
                        onSubmit={onCreateInvoice}
                        className="space-y-5"
                        data-testid="new-invoice-form"
                    >
                        <Input
                            label="Title"
                            testid="invoice-title"
                            value={invoiceForm.title}
                            onChange={(v) =>
                                setInvoiceForm((f) => ({ ...f, title: v }))
                            }
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Amount"
                                type="number"
                                testid="invoice-amount"
                                value={invoiceForm.amount}
                                onChange={(v) =>
                                    setInvoiceForm((f) => ({ ...f, amount: v }))
                                }
                            />
                            <Input
                                label="Currency"
                                testid="invoice-currency"
                                value={invoiceForm.currency}
                                onChange={(v) =>
                                    setInvoiceForm((f) => ({
                                        ...f,
                                        currency: v.toLowerCase(),
                                    }))
                                }
                            />
                        </div>
                        <Input
                            label="Payment instructions (optional — override studio default)"
                            testid="invoice-instructions"
                            multiline
                            value={invoiceForm.payment_instructions}
                            onChange={(v) =>
                                setInvoiceForm((f) => ({
                                    ...f,
                                    payment_instructions: v,
                                }))
                            }
                        />
                        <button
                            type="submit"
                            data-testid="invoice-submit"
                            className="btn-ember w-full h-12 text-xs uppercase tracking-[0.2em]"
                        >
                            Issue & email
                        </button>
                    </form>
                </Modal>
            )}
            {showEdit && (
                <Modal onClose={() => setShowEdit(false)} title="Edit client">
                    <form
                        onSubmit={onSaveEdit}
                        className="space-y-5"
                        data-testid="edit-client-form"
                    >
                        <label className="block">
                            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                                Plan tier
                            </span>
                            <select
                                value={editForm.plan}
                                onChange={(e) =>
                                    setEditForm((f) => ({
                                        ...f,
                                        plan: e.target.value,
                                    }))
                                }
                                data-testid="edit-plan"
                                className="w-full bg-ink border border-white/15 focus:border-ember outline-none px-3 h-10 text-bone text-xs uppercase tracking-[0.18em]"
                            >
                                <option value="">No plan</option>
                                <option value="Starter">Starter ($3,499/mo)</option>
                                <option value="Studio">Studio ($4,999/mo · 2 platforms · 1 lang)</option>
                <option value="Sprint">Sprint ($7,999/mo · 3 platforms · 1 lang)</option>
                                <option value="Custom">Custom</option>
                            </select>
                        </label>
                        <Input
                            label="Plan summary"
                            testid="edit-plan-summary"
                            multiline
                            value={editForm.plan_summary}
                            onChange={(v) =>
                                setEditForm((f) => ({
                                    ...f,
                                    plan_summary: v,
                                }))
                            }
                        />
                        <div className="pt-3 border-t border-white/10 space-y-5">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                                    Account manager
                                </p>
                                <div className="flex flex-col gap-2">
                                    <select
                                        value={data?.client?.manager_id || ""}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === "__auto__") {
                                                assignManager(null);
                                            } else if (v === "") {
                                                unassignManager();
                                            } else {
                                                assignManager(v);
                                            }
                                        }}
                                        data-testid="manager-picker"
                                        className="w-full bg-ink border border-white/15 focus:border-ember outline-none px-3 h-11 text-bone text-sm"
                                    >
                                        <option value="">— Unassigned —</option>
                                        <option value="__auto__">
                                            ⚡ Auto-assign (least loaded for {editForm.plan || "this tier"})
                                        </option>
                                        {team.length > 0 && (
                                            <optgroup label="Team members">
                                                {team
                                                    .filter((m) => m.active)
                                                    .map((m) => (
                                                        <option key={m.id} value={m.id}>
                                                            {m.name} · {m.role}
                                                            {" · "}
                                                            {m.client_count}/{m.max_clients} clients
                                                        </option>
                                                    ))}
                                            </optgroup>
                                        )}
                                    </select>
                                    {team.length === 0 && (
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-amber-400">
                                            No team members yet —{" "}
                                            <a
                                                href="/admin/team"
                                                className="underline hover:text-ember"
                                            >
                                                add one
                                            </a>
                                        </p>
                                    )}
                                    {data?.client?.manager_name && (
                                        <p className="text-xs text-bone-500">
                                            Currently assigned:{" "}
                                            <span className="text-bone">
                                                {data.client.manager_name}
                                            </span>{" "}
                                            ({data.client.manager_role})
                                        </p>
                                    )}
                                </div>
                            </div>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-bone-500">
                                Manual override (advanced)
                            </p>
                            <Input
                                label="Manager name"
                                testid="edit-manager-name"
                                value={editForm.manager_name}
                                onChange={(v) =>
                                    setEditForm((f) => ({
                                        ...f,
                                        manager_name: v,
                                    }))
                                }
                            />
                            <Input
                                label="Manager role"
                                testid="edit-manager-role"
                                value={editForm.manager_role}
                                onChange={(v) =>
                                    setEditForm((f) => ({
                                        ...f,
                                        manager_role: v,
                                    }))
                                }
                            />
                            <Input
                                label="Manager email"
                                type="email"
                                testid="edit-manager-email"
                                value={editForm.manager_email}
                                onChange={(v) =>
                                    setEditForm((f) => ({
                                        ...f,
                                        manager_email: v,
                                    }))
                                }
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="edit-submit"
                            className="btn-ember w-full h-12 text-xs uppercase tracking-[0.2em]"
                        >
                            Save changes
                        </button>
                    </form>
                </Modal>
            )}

            {client?.id && token && (
                <section className="mt-10" data-testid="admin-chat-section">
                    <div className="mb-5 flex items-center justify-between">
                        <h2 className="font-display text-bone text-2xl tracking-tight">
                            Conversation
                        </h2>
                        <span className="text-[11px] text-bone-500 uppercase tracking-[0.25em]">
                            {client.manager_name ? `Manager: ${client.manager_name}` : "No manager assigned"}
                        </span>
                    </div>
                    <ChatPanel
                        clientId={client.id}
                        token={token}
                        actorType="admin"
                        actorName="Aximo Studios"
                        title={`Thread with ${client.name}`}
                        subtitle="Visible to client and the assigned manager."
                    />
                </section>
            )}
        </AdminShell>
    );
}

function Modal({ children, onClose, title }) {
    return (
        <div className="fixed inset-0 z-40 bg-ink/80 backdrop-blur-sm flex items-center justify-center p-6">
            <div className="bg-ink-800 border border-white/15 w-full max-w-md p-8 relative">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 text-bone-500 hover:text-bone text-xl"
                >
                    ×
                </button>
                <h2 className="font-display text-bone text-2xl tracking-tight mb-6">
                    {title}
                </h2>
                {children}
            </div>
        </div>
    );
}

function Input({ label, type = "text", value, onChange, testid, multiline }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            {multiline ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={3}
                    data-testid={testid}
                    className="w-full bg-transparent border border-white/15 focus:border-ember outline-none p-3 text-bone text-sm resize-none"
                />
            ) : (
                <input
                    type={type}
                    value={value}
                    step={type === "number" ? "0.01" : undefined}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testid}
                    className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-2 text-bone font-display text-base"
                />
            )}
        </label>
    );
}
